// The home_live snapshot: one row the database refreshes every 15 minutes
// (cron home-live-refresh). The board reads its headline counts and the pay
// bands from here so it agrees with auditfriendly.co, which reads the same row.
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type PayFamily = { n: number; med: number; p25: number; p75: number };

export type HomeLive = {
  stats?: { active_jobs?: number; remote_active?: number; remote_with_pay?: number };
  strip?: { new_24h?: number; remote_new_24h?: number };
  pay?: { families?: Record<string, PayFamily> };
  computed_at?: string;
};

export function useHomeLive() {
  return useQuery<HomeLive | null>({
    queryKey: ["home-live"],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as unknown as (fn: string) => Promise<{ data: unknown; error: unknown }>)("home_live");
      if (error) return null;
      return (data as HomeLive) ?? null;
    },
  });
}

// Which pay family a title belongs to. Deliberately narrow: a title that does
// not clearly match gets no band rather than a wrong one.
const FAMILY_RULES: [RegExp, string][] = [
  [/\bcontroller\b/i, "Controller"],
  [/\btax\b/i, "Tax"],
  [/\b(fp&a|fp ?& ?a|financial planning)\b/i, "FP&A"],
  [/\bpayroll\b/i, "Payroll"],
  [/\b(accounts payable|accounts receivable|ap\/ar|ap specialist|ar specialist|ap clerk|ar clerk)\b/i, "AP / AR"],
  [/\baccountant\b/i, "Accountant"],
];

export function payFamilyFor(title: string | null | undefined): string | null {
  if (!title) return null;
  if (/\b(chief|cfo|vp|vice president|director|head of)\b/i.test(title)) return null; // above the family's range
  for (const [re, fam] of FAMILY_RULES) if (re.test(title)) return fam;
  return null;
}
